import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MODERN_ALERT_EVENT,
  MODERN_CONFIRM_EVENT,
  MODERN_PROMPT_EVENT,
  modernAlert,
  modernConfirm,
  modernPrompt,
} from "./modernDialog";
import { claimModernDialogHost, releaseModernDialogHost } from "./modernDialogHostRegistry";
import "../../shared/styles/modernDialogHost.css";

const ModernDialogHost = () => {
  const hostIdRef = useRef(`mdh-${Math.random().toString(36).slice(2)}`);
  const [alerts, setAlerts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const [promptState, setPromptState] = useState(null);
  const [promptValue, setPromptValue] = useState("");
  const isOwner = useRef(claimModernDialogHost(hostIdRef.current)).current;

  useEffect(() => {
    if (!isOwner) return undefined;
    const hostId = hostIdRef.current;

    const onAlert = (event) => {
      const message = String(event?.detail?.message ?? "");
      if (!message) return;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setAlerts((prev) => [...prev, { id, message }]);
      window.setTimeout(() => {
        setAlerts((prev) => prev.filter((item) => item.id !== id));
      }, 4000);
    };

    const onConfirm = (event) => {
      const detail = event?.detail || {};
      setConfirmState({
        message: detail.message,
        title: detail.title || "Confirm",
        confirmLabel: detail.confirmLabel || "Confirm",
        cancelLabel: detail.cancelLabel || "Cancel",
        danger: Boolean(detail.danger),
        resolve: detail.resolve,
      });
    };

    const onPrompt = (event) => {
      const detail = event?.detail || {};
      setPromptValue(String(detail.defaultValue ?? ""));
      setPromptState({
        message: detail.message,
        title: detail.title || "Enter value",
        confirmLabel: detail.confirmLabel || "OK",
        cancelLabel: detail.cancelLabel || "Cancel",
        resolve: detail.resolve,
      });
    };

    const originalAlert = window.alert;
    const originalConfirm = window.confirm;
    const originalPrompt = window.prompt;

    // Route all native dialogs through the modern overlay.
    window.alert = (message) => {
      modernAlert(message);
    };
    window.confirm = (message) => modernConfirm(String(message ?? ""));
    window.prompt = (message, defaultValue) =>
      modernPrompt(String(message ?? ""), { defaultValue: defaultValue ?? "" });

    window.addEventListener(MODERN_ALERT_EVENT, onAlert);
    window.addEventListener(MODERN_CONFIRM_EVENT, onConfirm);
    window.addEventListener(MODERN_PROMPT_EVENT, onPrompt);

    return () => {
      window.removeEventListener(MODERN_ALERT_EVENT, onAlert);
      window.removeEventListener(MODERN_CONFIRM_EVENT, onConfirm);
      window.removeEventListener(MODERN_PROMPT_EVENT, onPrompt);
      window.alert = originalAlert;
      window.confirm = originalConfirm;
      window.prompt = originalPrompt;
      releaseModernDialogHost(hostId);
    };
  }, [isOwner]);

  if (!isOwner) return null;

  const closeConfirm = (value) => {
    if (confirmState?.resolve) confirmState.resolve(Boolean(value));
    setConfirmState(null);
  };

  const closePrompt = (value) => {
    if (promptState?.resolve) promptState.resolve(value);
    setPromptState(null);
    setPromptValue("");
  };

  return (
    <>
      {alerts.length > 0 ? (
        <div className="enterprise-modern-toast-stack" aria-live="polite">
          {alerts.map((alert) => (
            <div key={alert.id} className="enterprise-modern-toast">
              <button
                type="button"
                className="enterprise-modern-toast__close"
                onClick={() => setAlerts((prev) => prev.filter((item) => item.id !== alert.id))}
                aria-label="Close"
              >
                ×
              </button>
              <p className="enterprise-modern-toast__message">{alert.message}</p>
            </div>
          ))}
        </div>
      ) : null}

      {confirmState
        ? createPortal(
            <div
              className="app-modal-overlay enterprise-modern-dialog-overlay"
              role="presentation"
              onClick={() => closeConfirm(false)}
            >
              <div
                className="app-modal-panel enterprise-modern-dialog"
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="app-modal-header">
                  <h3 className="app-modal-title">{confirmState.title}</h3>
                </div>
                <div className="app-modal-body">
                  <p className="enterprise-modern-dialog__message">{confirmState.message}</p>
                </div>
                <div className="app-modal-footer enterprise-modern-dialog__actions">
                  <button
                    type="button"
                    className="app-modal-btn-ghost"
                    onClick={() => closeConfirm(false)}
                  >
                    {confirmState.cancelLabel}
                  </button>
                  <button
                    type="button"
                    className={confirmState.danger ? "app-modal-btn-danger" : "app-modal-btn-primary"}
                    onClick={() => closeConfirm(true)}
                  >
                    {confirmState.confirmLabel}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {promptState
        ? createPortal(
            <div
              className="app-modal-overlay enterprise-modern-dialog-overlay"
              role="presentation"
              onClick={() => closePrompt(null)}
            >
              <div
                className="app-modal-panel enterprise-modern-dialog"
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="app-modal-header">
                  <h3 className="app-modal-title">{promptState.title}</h3>
                </div>
                <div className="app-modal-body">
                  {promptState.message ? (
                    <p className="enterprise-modern-dialog__message">{promptState.message}</p>
                  ) : null}
                  <input
                    type="text"
                    className="enterprise-modern-dialog__input"
                    value={promptValue}
                    onChange={(e) => setPromptValue(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="app-modal-footer enterprise-modern-dialog__actions">
                  <button type="button" className="app-modal-btn-ghost" onClick={() => closePrompt(null)}>
                    {promptState.cancelLabel}
                  </button>
                  <button
                    type="button"
                    className="app-modal-btn-primary"
                    onClick={() => closePrompt(promptValue)}
                  >
                    {promptState.confirmLabel}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export default ModernDialogHost;
